import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, ZoomControl, useMap } from "react-leaflet";
import { feature as topojsonFeature } from "topojson-client";
import { ArrowLeft, MapPinned } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { getCompanySuggestions, getMapDistrictMetrics, getMapStateMetrics } from "../lib/api";
import {
  buildMetricStops,
  formatCompactMapMetric,
  formatMapMetric,
  getMapFillColor,
  getMapMetricValue,
  normalizeMapJoinKey,
  slugifyMapLabel,
} from "../lib/mapJoin";

const topoCache = new Map();
const INDIA_CENTER = [22.7, 80.3];
const INDIA_ZOOM = 4.5;

const loadTopology = async (scope) => {
  const url = scope === "states" ? "/topo/states.json" : "/topo/districts.json";
  if (!topoCache.has(url)) {
    topoCache.set(url, fetch(url).then((response) => response.json()));
  }
  return topoCache.get(url);
};

const buildMapParams = (appliedQuery, appliedFilters) => ({
  ...(appliedQuery ? { search: appliedQuery } : {}),
  ...(appliedFilters.company ? { company: appliedFilters.company } : {}),
  ...(appliedFilters.company_id ? { company_id: appliedFilters.company_id } : {}),
  ...(appliedFilters.sector !== "all" ? { sector: appliedFilters.sector } : {}),
  ...(appliedFilters.year !== "all" ? { year: appliedFilters.year } : {}),
  ...(appliedFilters.implementation_mode !== "all" ? { implementation_mode: appliedFilters.implementation_mode } : {}),
});

const buildCompanySuggestionParams = (filters) => ({
  query: filters.company,
  ...(filters.state !== "all" ? { state: filters.state } : {}),
  ...(filters.district !== "all" ? { district: filters.district } : {}),
  ...(filters.sector !== "all" ? { sector: filters.sector } : {}),
  ...(filters.year !== "all" ? { year: filters.year } : {}),
  ...(filters.implementation_mode !== "all" ? { implementation_mode: filters.implementation_mode } : {}),
});

const findMetricForFeature = (scope, properties, lookups) => {
  if (scope === "states") {
    return lookups.byJoinKey.get(normalizeMapJoinKey(properties.state_name));
  }

  const byDistrictCode = lookups.byDistrictCode.get(String(properties.district_lgd || ""));
  if (byDistrictCode) return byDistrictCode;
  return lookups.byJoinKey.get(normalizeMapJoinKey(properties.district_name));
};

const MapLayerController = ({ scope, geoJson, lookups, metricMode, selectedDistrict, onHoverChange, onSelectRegion }) => {
  const map = useMap();
  const layerRef = useRef(null);
  const lastBoundsKeyRef = useRef("");

  useEffect(() => {
    if (!map || !geoJson) return undefined;

    const metricValues = lookups.items.map((item) => getMapMetricValue(item, metricMode));
    const stops = buildMetricStops(metricValues);
    map.stop();

    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }

    layerRef.current = L.geoJSON(geoJson, {
      style: (featureData) => {
        const item = findMetricForFeature(scope, featureData.properties || {}, lookups);
        const isSelected = scope === "districts" && selectedDistrict && item?.district_filter_value === selectedDistrict;
        return {
          fillColor: getMapFillColor(getMapMetricValue(item, metricMode), stops),
          fillOpacity: item ? 0.88 : 0.55,
          color: isSelected ? "#1E3A8A" : "#FFFFFF",
          weight: isSelected ? 2.6 : scope === "states" ? 1.25 : 0.95,
          opacity: 1,
        };
      },
      onEachFeature: (featureData, layer) => {
        const properties = featureData.properties || {};
        const item = findMetricForFeature(scope, properties, lookups);
        const label = properties.district_name || properties.state_name || "Region";

        layer.on("add", () => {
          const element = layer.getElement?.();
          if (element) {
            element.setAttribute("data-testid", `india-map-region-${slugifyMapLabel(label)}`);
            element.style.cursor = item?.project_count ? "pointer" : "default";
          }
        });

        layer.on("mouseover", () => {
          onHoverChange({
            item,
            label,
            scope,
          });
          layer.setStyle({ color: "#0F172A", weight: scope === "states" ? 2 : 1.6 });
          layer.bringToFront?.();
        });

        layer.on("mouseout", () => {
          onHoverChange(null);
          layerRef.current?.resetStyle(layer);
        });

        if (item?.project_count) {
          layer.on("click", () => onSelectRegion(item));
        }
      },
    }).addTo(map);

    const bounds = layerRef.current.getBounds();
    const firstFeature = geoJson.features[0]?.properties || {};
    const boundsKey = `${scope}-${geoJson.features.length}-${firstFeature.state_name || ""}-${firstFeature.district_name || ""}`;
    if (bounds.isValid() && lastBoundsKeyRef.current !== boundsKey) {
      map.fitBounds(bounds, { padding: [18, 18], animate: false });
      lastBoundsKeyRef.current = boundsKey;
    } else if (!bounds.isValid() && !lastBoundsKeyRef.current) {
      map.setView(INDIA_CENTER, INDIA_ZOOM);
    }

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [geoJson, lookups, map, metricMode, onHoverChange, onSelectRegion, scope, selectedDistrict]);

  return null;
};

export default function IndiaGrantMap({
  appliedQuery,
  filters,
  appliedFilters,
  filterOptions,
  activeFilterCount,
  bootstrapping,
  onCompanyInputChange,
  onCompanySuggestionSelect,
  onApplyState,
  onApplyDistrict,
  onFilterChange,
  onResetFilters,
  onClearDistrict,
  onClearGeography,
  onShowAllRecords,
}) {
  const [metricMode, setMetricMode] = useState("spend");
  const [mapData, setMapData] = useState({ geoJson: null, metrics: null });
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const scope = appliedFilters.state !== "all" ? "districts" : "states";
  const selectedDistrict = appliedFilters.district !== "all" ? appliedFilters.district : null;

  useEffect(() => {
    let cancelled = false;

    const loadMap = async () => {
      setLoading(true);
      setError("");

      try {
        const params = buildMapParams(appliedQuery, appliedFilters);
        const [topology, metrics] = await Promise.all([
          loadTopology(scope),
          scope === "states"
            ? getMapStateMetrics(params)
            : getMapDistrictMetrics({ ...params, state: appliedFilters.state }),
        ]);

        if (cancelled) return;

        const topologyObjectName = scope === "states" ? "states" : "districts";
        const fullGeoJson = topojsonFeature(topology, topology.objects[topologyObjectName]);
        const filteredFeatures =
          scope === "states"
            ? fullGeoJson.features
            : fullGeoJson.features.filter(
                (featureData) => normalizeMapJoinKey(featureData.properties?.state_name) === normalizeMapJoinKey(appliedFilters.state),
              );

        setMapData({
          geoJson: {
            ...fullGeoJson,
            features: filteredFeatures,
          },
          metrics,
        });
        setHoveredRegion(null);
      } catch (loadError) {
        if (!cancelled) {
          setError("We couldn’t load the India map right now. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMap();
    return () => {
      cancelled = true;
    };
  }, [appliedFilters.company, appliedFilters.company_id, appliedFilters.implementation_mode, appliedFilters.sector, appliedFilters.state, appliedFilters.year, appliedQuery, scope]);

  useEffect(() => {
    let cancelled = false;
    const queryValue = filters.company.trim();

    if (queryValue.length < 2) {
      setCompanySuggestions([]);
      return undefined;
    }

    const loadSuggestions = async () => {
      try {
        const response = await getCompanySuggestions(buildCompanySuggestionParams(filters));
        if (!cancelled) {
          setCompanySuggestions((response.items || []).filter((item) => item.company_name.toLowerCase() !== queryValue.toLowerCase()));
        }
      } catch {
        if (!cancelled) {
          setCompanySuggestions([]);
        }
      }
    };

    loadSuggestions();
    return () => {
      cancelled = true;
    };
  }, [filters.company, filters.district, filters.implementation_mode, filters.sector, filters.state, filters.year]);

  const metricItems = useMemo(() => mapData.metrics?.items || [], [mapData.metrics]);
  const lookups = useMemo(() => {
    const byJoinKey = new Map();
    const byDistrictCode = new Map();

    metricItems.forEach((item) => {
      byJoinKey.set(item.key, item);
      if (item.district_lgd) {
        byDistrictCode.set(String(item.district_lgd), item);
      }
    });

    return { items: metricItems, byJoinKey, byDistrictCode };
  }, [metricItems]);

  const sortedItems = useMemo(
    () => [...metricItems].sort((left, right) => getMapMetricValue(right, metricMode) - getMapMetricValue(left, metricMode)).slice(0, 5),
    [metricItems, metricMode],
  );

  const handleRegionSelect = useCallback(
    (item) => {
      if (scope === "states") {
        onApplyState(item.state_filter_value || item.name);
        return;
      }

      onApplyDistrict(item.district_filter_value || item.name);
    },
    [onApplyDistrict, onApplyState, scope],
  );

  const selectedRegion = selectedDistrict ? metricItems.find((item) => item.district_filter_value === selectedDistrict) || null : null;
  const focusRegion = selectedRegion || hoveredRegion?.item || null;
  const focusLabel = selectedDistrict || hoveredRegion?.label || (scope === "districts" ? appliedFilters.state : "India");
  const summary = mapData.metrics?.summary;
  const filterGroups = [
    { key: "sector", label: "Sector", options: filterOptions?.sectors || [], testId: "map-filter-sector-select" },
    { key: "year", label: "Financial year", options: filterOptions?.years || [], testId: "map-filter-year-select" },
    {
      key: "implementation_mode",
      label: "Implementation mode",
      options: filterOptions?.implementation_modes || [],
      testId: "map-filter-implementation-mode-select",
    },
  ];
  const summaryCards = [
    { label: "Mapped spend", value: formatCompactMapMetric(summary?.mapped_total_spend_crore, "spend"), testId: "india-map-summary-mapped-spend" },
    { label: "Mapped projects", value: formatCompactMapMetric(summary?.mapped_project_count, "projects"), testId: "india-map-summary-mapped-projects" },
    { label: "Outside map coverage", value: formatCompactMapMetric(summary?.unmapped_total_spend_crore, "spend"), testId: "india-map-summary-unmapped-spend" },
    {
      label: "Pan-India spend",
      subtitle: "Not considered in India Map",
      value: formatCompactMapMetric(summary?.pan_india_total_spend_crore, "spend"),
      testId: "india-map-summary-pan-india-spend",
    },
    {
      label: "Not mentioned districts spend",
      subtitle: "Not considered in India Map",
      value: formatCompactMapMetric(summary?.not_mentioned_district_total_spend_crore, "spend"),
      testId: "india-map-summary-not-mentioned-spend",
    },
  ];

  return (
    <Card className="section-frame rounded-none border-slate-200" data-testid="india-grant-map-section">
      <CardContent className="space-y-6 p-6 md:p-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Geographic drilldown</p>
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-950" data-testid="india-map-heading">
                Explore CSR activity visually, then drill from state to district.
              </h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-600" data-testid="india-map-description">
                Click a state to switch the explorer into district mode. The map follows the same active search filters already applied to your results.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={metricMode === "spend" ? "default" : "outline"}
              className={`rounded-none ${metricMode === "spend" ? "bg-[#F26522] text-white hover:bg-[#D9561B]" : "border-slate-300"}`}
              onClick={() => setMetricMode("spend")}
              data-testid="map-metric-spend-button"
            >
              Total spend
            </Button>
            <Button
              type="button"
              variant={metricMode === "projects" ? "default" : "outline"}
              className={`rounded-none ${metricMode === "projects" ? "bg-[#1E3A8A] text-white hover:bg-[#17316F]" : "border-slate-300"}`}
              onClick={() => setMetricMode("projects")}
              data-testid="map-metric-projects-button"
            >
              Project count
            </Button>
            {scope === "districts" ? (
              <Button type="button" variant="outline" className="rounded-none border-slate-300" onClick={onClearGeography} data-testid="map-back-to-india-button">
                <ArrowLeft className="mr-2" size={16} />
                Back to India
              </Button>
            ) : null}
            {selectedDistrict ? (
              <Button type="button" variant="outline" className="rounded-none border-slate-300" onClick={onClearDistrict} data-testid="map-clear-district-button">
                Clear district
              </Button>
            ) : null}
          </div>
        </div>

        <div className="border border-slate-200 bg-slate-50 p-5" data-testid="india-map-filter-bar">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(280px,1.2fr)_repeat(3,minmax(0,1fr))] xl:flex-1">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Company</label>
                <Input
                  value={filters.company}
                  onChange={(event) => onCompanyInputChange(event.target.value)}
                  placeholder="Filter by company name"
                  className="h-12 rounded-none border-slate-300 bg-white text-sm"
                  data-testid="map-filter-company-input"
                  disabled={bootstrapping}
                />
                {companySuggestions.length > 0 ? (
                  <div className="max-h-52 overflow-y-auto border border-slate-200 bg-white" data-testid="map-company-suggestions-list">
                    {companySuggestions.map((suggestion) => (
                      <button
                        key={suggestion.company_id}
                        type="button"
                        className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                        onClick={() => {
                          setCompanySuggestions([]);
                          onCompanySuggestionSelect(suggestion);
                        }}
                        data-testid={`map-company-suggestion-${slugifyMapLabel(suggestion.company_name)}`}
                      >
                        <span className="block font-medium text-slate-900">{suggestion.company_name}</span>
                        <span className="mt-1 block text-xs text-slate-500">{suggestion.cin || "CIN not available"}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              {filterGroups.map((group) => (
                <div key={group.key} className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{group.label}</label>
                  <Select value={filters[group.key]} onValueChange={(value) => onFilterChange(group.key, value)} disabled={bootstrapping}>
                    <SelectTrigger className="h-12 rounded-none border-slate-300 bg-white text-sm" data-testid={group.testId}>
                      <SelectValue placeholder={`Select ${group.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent side="top" sideOffset={8} position="popper" className="max-h-80 rounded-none">
                      <SelectItem value="all">All {group.label}s</SelectItem>
                      {group.options.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              <div className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600" data-testid="india-map-active-filter-count">
                {activeFilterCount} active filters
              </div>
              <Button type="button" variant="outline" className="h-12 rounded-none border-slate-300" onClick={onResetFilters} data-testid="india-map-reset-filters-button">
                Reset filters
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="india-map-summary-grid">
          {summaryCards.map((card) => (
            <div key={card.label} className="border border-slate-200 bg-slate-50 p-4" data-testid={card.testId}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{card.label}</p>
              {card.subtitle ? <p className="mt-1 text-xs text-slate-500" data-testid={`${card.testId}-subtitle`}>{card.subtitle}</p> : null}
              <p className="mt-2 text-2xl font-black text-slate-950">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.9fr)]">
          <div className="overflow-hidden border border-slate-200 bg-white" data-testid="india-map-canvas-panel">
            {error ? (
              <div className="flex min-h-[520px] items-center justify-center px-6 text-center text-sm text-red-700" data-testid="india-map-error-message">
                {error}
              </div>
            ) : loading || !mapData.geoJson ? (
              <div className="flex min-h-[520px] items-center justify-center px-6 text-center text-sm text-slate-600" data-testid="india-map-loading-state">
                Loading the interactive map…
              </div>
            ) : (
              <MapContainer
                center={INDIA_CENTER}
                zoom={INDIA_ZOOM}
                zoomSnap={0.25}
                scrollWheelZoom={false}
                attributionControl={false}
                zoomControl={false}
                className="h-[520px] w-full"
                data-testid="india-map-canvas"
              >
                <ZoomControl position="bottomright" />
                <MapLayerController
                  scope={scope}
                  geoJson={mapData.geoJson}
                  lookups={lookups}
                  metricMode={metricMode}
                  selectedDistrict={selectedDistrict}
                  onHoverChange={setHoveredRegion}
                  onSelectRegion={handleRegionSelect}
                />
              </MapContainer>
            )}
          </div>

          <div className="space-y-4">
            <div className="border border-slate-200 bg-slate-50 p-5" data-testid="india-map-focus-panel">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">Currently in view</p>
                  <h3 className="mt-2 text-2xl font-bold text-slate-950" data-testid="india-map-focus-label">{focusLabel}</h3>
                </div>
                <MapPinned className="text-[#F26522]" size={20} />
              </div>
              <p className="mt-3 text-sm text-slate-600" data-testid="india-map-focus-helper">
                {focusRegion
                  ? `${formatMapMetric(getMapMetricValue(focusRegion, metricMode), metricMode)} across ${focusRegion.company_count.toLocaleString("en-IN")} companies.`
                  : scope === "states"
                    ? "Hover over a state or use the quick picks to jump into a high-activity geography."
                    : "Hover over a district or use the quick picks to focus the records table further."}
              </p>
              {scope === "districts" ? (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 rounded-full border-slate-300 px-4"
                  onClick={onShowAllRecords}
                  data-testid="map-show-all-records-chip"
                >
                  Show all records
                </Button>
              ) : null}
            </div>

            <div className="border border-slate-200 bg-white p-5" data-testid="india-map-quick-picks-panel">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">Quick picks</p>
                  <h3 className="mt-2 text-lg font-bold text-slate-950">Top geographies in this view</h3>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500" data-testid="india-map-quick-picks-metric-label">
                  {metricMode === "spend" ? "Spend" : "Projects"}
                </span>
              </div>

              <div className="mt-4 space-y-2">
                {sortedItems.map((item) => (
                  <button
                    key={`${scope}-${item.key}`}
                    type="button"
                    className="flex w-full items-center justify-between border border-slate-200 bg-slate-50 px-4 py-3 text-left transition-colors hover:border-[#F26522] hover:bg-white"
                    onClick={() => handleRegionSelect(item)}
                    data-testid={`india-map-quick-pick-${slugifyMapLabel(item.name)}`}
                  >
                    <span className="font-semibold text-slate-900">{item.name}</span>
                    <span className="text-sm font-bold text-[#1E3A8A]">{formatCompactMapMetric(getMapMetricValue(item, metricMode), metricMode)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600" data-testid="india-map-coverage-note">
              {summary?.unmapped_project_count
                ? (
                  <div className="space-y-3">
                    <p>
                      {summary.unmapped_project_count.toLocaleString("en-IN")} records sit outside the visible polygons. They are still counted in the KPIs above, so they should not be read as missing data.
                    </p>
                    <div className="space-y-2 text-xs text-slate-600" data-testid="india-map-coverage-breakdown">
                      {summary.pan_india_project_count ? (
                        <div data-testid="india-map-coverage-pan-india-row">
                          <span className="font-semibold text-slate-700">Pan-India filings:</span>{" "}
                          {summary.pan_india_project_count.toLocaleString("en-IN")} records · {formatCompactMapMetric(summary.pan_india_total_spend_crore, "spend")}
                        </div>
                      ) : null}
                      {summary.not_mentioned_district_project_count ? (
                        <div data-testid="india-map-coverage-not-mentioned-row">
                          <span className="font-semibold text-slate-700">District marked “Not mentioned”:</span>{" "}
                          {summary.not_mentioned_district_project_count.toLocaleString("en-IN")} records · {formatCompactMapMetric(summary.not_mentioned_district_total_spend_crore, "spend")}
                        </div>
                      ) : null}
                      {summary.other_unmapped_project_count ? (
                        <div data-testid="india-map-coverage-other-unmapped-row">
                          <span className="font-semibold text-slate-700">Other unmatched geography:</span>{" "}
                          {summary.other_unmapped_project_count.toLocaleString("en-IN")} records · {formatCompactMapMetric(summary.other_unmapped_total_spend_crore, "spend")}
                        </div>
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-500">
                      Pan-India filings never land on a polygon, and district values marked `Not mentioned` are kept out of district drilldowns to avoid false precision.
                    </p>
                  </div>
                )
                : "All currently filtered records are represented on the visible map layer."}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}